import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import adminAxios from "../../../api/adminAxios";
import * as XLSX from "xlsx";

const dt = (d) => (d ? new Date(d).toLocaleString("en-IN") : "N/A");

const ConfirmedOrders = () => {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setErr("");

        const res = await adminAxios.get("/api/admin/orders/confirmed");
        setOrders(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        setErr(
          e.response?.data?.message ||
            e.message ||
            "Failed to fetch confirmed orders"
        );
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  // =========================
  // ✅ EXCEL DOWNLOAD FUNCTION
  // =========================
  const downloadExcel = () => {
    if (!orders.length) return;

    const formattedData = orders.map((o) => ({
      "Order ID": o.orderId || "N/A",
      "Status": o.latestStatus?.status || "Confirmed",
      "Date": dt(o.latestStatus?.timestamp || o.createdAt),

      "User Name": o.userName || "N/A",
      "User Email": o.userEmail || "N/A",
      "User Mobile": o.userMobile || "N/A",

      "Seller Name": o.sellerName || "N/A",
      "Seller Email": o.sellerEmail || "N/A",
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Confirmed Orders"
    );

    // ✅ Direct download
    XLSX.writeFile(
      workbook,
      `Confirmed_Orders_${Date.now()}.xlsx`
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <p className="text-lg font-semibold text-gray-600">
          Loading Confirmed Orders...
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">

        {/* HEADER */}
        <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900">
            Confirmed Orders
          </h1>

          <div className="flex gap-2">
            {/* ✅ DOWNLOAD BUTTON */}
            <button
              onClick={downloadExcel}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              ⬇ Download Excel
            </button>

            <button
              className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
              onClick={() => navigate(-1)}
            >
              Back
            </button>
          </div>
        </div>

        {err && (
          <div className="mb-4 bg-white border border-red-200 rounded p-4 text-red-700">
            {err}
          </div>
        )}

        {/* TABLE */}
        <div className="bg-white border rounded-lg shadow-sm overflow-x-auto">
          {orders.length > 0 ? (
            <table className="w-full text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-5 py-3 text-left">Order ID</th>
                  <th className="px-5 py-3 text-left">User</th>
                  <th className="px-5 py-3 text-left">Seller</th>
                  <th className="px-5 py-3 text-left">Date</th>
                  <th className="px-5 py-3 text-left">Status</th>
                  <th className="px-5 py-3 text-left">Action</th>
                </tr>
              </thead>

              <tbody>
                {orders.map((o, idx) => (
                  <tr
                    key={o._id || idx}
                    className="border-t hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelected(o)}
                  >
                    <td className="px-5 py-4 font-medium">
                      {o.orderId || "N/A"}
                    </td>

                    <td className="px-5 py-4">
                      <div className="font-semibold">
                        {o.userName || "N/A"}
                      </div>
                      <div className="text-xs text-gray-500">
                        {o.userEmail}
                      </div>
                      <div className="text-xs text-gray-500">
                        {o.userMobile}
                      </div>
                    </td>

                    <td className="px-5 py-4">
                      <div className="font-semibold">
                        {o.sellerName || "N/A"}
                      </div>
                      <div className="text-xs text-gray-500">
                        {o.sellerEmail}
                      </div>
                    </td>

                    <td className="px-5 py-4">
                      {dt(o.latestStatus?.timestamp || o.createdAt)}
                    </td>

                    <td className="px-5 py-4 text-blue-700 font-semibold">
                      {o.latestStatus?.status || "Confirmed"}
                    </td>

                    <td className="px-5 py-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/orders/${o.orderId}`);
                        }}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-6 text-center text-gray-500">
              No confirmed orders found.
            </div>
          )}
        </div>

        {/* MODAL */}
        {selected && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
            <div className="w-full max-w-xl bg-white rounded-lg shadow-lg border">
              <div className="p-4 border-b flex justify-between">
                <div className="font-bold">Order Quick Details</div>
                <button onClick={() => setSelected(null)}>Close</button>
              </div>

              <div className="p-4 text-sm space-y-2">
                <div>Order ID: {selected.orderId}</div>
                <div>Status: {selected.latestStatus?.status}</div>
                <div>Date: {dt(selected.latestStatus?.timestamp)}</div>
                <div>User: {selected.userName}</div>
                <div>Seller: {selected.sellerName}</div>
              </div>

              <div className="p-4 border-t flex justify-end gap-2">
                <button onClick={() => setSelected(null)}>Close</button>
                <button
                  onClick={() =>
                    navigate(`/orders/${selected.orderId}`)
                  }
                  className="bg-blue-600 text-white px-4 py-2 rounded"
                >
                  Full View
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfirmedOrders;